import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { MutationFunction } from '@tanstack/react-query';
import { Page } from 'src/services/documents/types';
import { VersionType } from './enums';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface SetBulkDocumentUpdateParams {
  documents: {
    documentId: string;
    documentTypeId?: string; // Optional, used for new documents
    loanId: string;
    versionIdentifier: VersionType;
    metadata?: {
      external_document_filename?: string;
      approved?: boolean;
    };
    documentPages: Page[];
    documentTypeConfidence: number; // Optional, used for new documents
    isDeleted: boolean; // Indicates if the document is being deleted
    userApproved: boolean;
  }[];
}

export interface BulkDocumentUpdateDocument {
  id: string;
  documentPages?: {
    pageId: string;
  }[];
  isNew?: boolean;
  isDeleted?: boolean;
}

export interface BulkDocumentUpdateResponse {
  success: boolean;
  documents: BulkDocumentUpdateDocument[];
  message?: string;
}

type BulkDocumentUpdateApiResponse =
  | BulkDocumentUpdateResponse
  | BulkDocumentUpdateDocument[];

const setBulkDocumentUpdateType: MutationFunction<
  BulkDocumentUpdateResponse,
  SetBulkDocumentUpdateParams
> = async (args) => {
  if (isMockServices()) return await mock(_mockType);
  const rqBody = args.documents.map((doc) => ({
    ...(doc.documentId == '' ? {} : { documentId: doc.documentId }),
    documentTypeConfidence: doc.documentTypeConfidence,
    metadata: doc.metadata,
    loanId: doc.loanId,
    documentTypeId: doc.documentTypeId,
    isDeleted: doc.isDeleted,
    versionIdentifier: doc.versionIdentifier,
    documentPages: doc.documentPages,
    userApproved: doc.userApproved ?? false,
  }));
  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<BulkDocumentUpdateApiResponse>(
    `/documents`,
    {
      method: 'PUT',
      body: rqBody,
    },
  );

  if (Array.isArray(response.data)) {
    return {
      success: response.success,
      documents: response.data,
    };
  }

  if (response.data) {
    return {
      success:
        typeof response.data.success === 'boolean'
          ? response.data.success
          : response.success,
      documents: response.data.documents ?? [],
      message: response.data.message,
    };
  }
  throw new Error('Failed to update documents');
};

export default setBulkDocumentUpdateType;

const _mockType = {
  success: true,
  documents: [],
};
